import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Lock, LockOpen, ShieldWarning, Warning, ArrowsClockwise } from "@phosphor-icons/react";
import { useAuth } from "@/lib/AuthContext";
import { formatLocal } from "@/lib/timezone";
import SecuritySummaryWidget from "@/components/SecuritySummaryWidget";

const REASON_LABELS = {
  login_failed: "Wrong password / unknown email",
  login_blocked_disabled: "Account disabled",
  login_blocked_non_eyvo: "Non-@eyvo.com Google sign-in blocked",
  login_blocked_domain: "Client domain not in tenant allow-list",
};

function SectionHeader({ icon: Icon, title, subtitle, onRefresh, refreshing }) {
  return (
    <div className="flex items-start justify-between mb-4 gap-3">
      <div>
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] flex items-center gap-1.5">
          <Icon size={14} />{title}
        </h2>
        {subtitle && <p className="text-xs text-[#525252] mt-0.5">{subtitle}</p>}
      </div>
      {onRefresh && (
        <Button onClick={onRefresh} disabled={refreshing} size="sm"
                className="bg-white border border-[#E5E5E5] text-[#0A0A0A] hover:border-[#0055FF] hover:text-[#0055FF] rounded-none h-8 px-3 text-[11px] uppercase tracking-wider"
                data-testid="security-refresh-btn">
          <ArrowsClockwise size={12} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      )}
    </div>
  );
}

// ── Failed Logins ─────────────────────────────────────────────────────────

function FailedLogins() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], top_offenders: [] });
  const [hours, setHours] = useState(24);
  const [scope, setScope] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { hours, limit: 300 };
      if (scope) params.scope = scope;
      if (emailFilter.trim()) params.email = emailFilter.trim();
      const r = await api.get("/admin/security/failed-logins", { params });
      setData(r.data || { items: [], top_offenders: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load failed logins");
    }
    setLoading(false);
  }, [hours, scope, emailFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="border border-[#E5E5E5] rounded-none p-5" data-testid="failed-logins-card">
      <SectionHeader icon={ShieldWarning}
                     title="Failed Login Attempts"
                     subtitle="Rolling view of every rejected authentication from external_activity_log."
                     onRefresh={load} refreshing={loading} />

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <label className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">Window</label>
        <select value={hours} onChange={e => setHours(Number(e.target.value))}
                className="h-8 px-2 text-xs border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]"
                data-testid="failed-login-hours">
          <option value={1}>1 hour</option>
          <option value={6}>6 hours</option>
          <option value={24}>24 hours</option>
          <option value={72}>3 days</option>
          <option value={168}>7 days</option>
        </select>
        <label className="text-[10px] font-bold uppercase tracking-wider text-[#525252] ml-2">Scope</label>
        <select value={scope} onChange={e => setScope(e.target.value)}
                className="h-8 px-2 text-xs border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]"
                data-testid="failed-login-scope">
          <option value="">All</option>
          <option value="eyvo">Eyvo staff</option>
          <option value="client">Client users</option>
        </select>
        <Input placeholder="Filter by email…"
               value={emailFilter}
               onChange={e => setEmailFilter(e.target.value)}
               onKeyDown={e => e.key === "Enter" && load()}
               className="rounded-none border-[#E5E5E5] h-8 w-56 text-xs"
               data-testid="failed-login-email-filter" />
      </div>

      {/* Top offenders */}
      {data.top_offenders?.length > 0 && (
        <div className="mb-5 border border-[#FEF2F2] bg-[#FEF9F9] p-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#991B1B] mb-2">
            Top offenders — {hours}h window
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.top_offenders.slice(0, 12).map((o, i) => (
              <div key={i} className="flex items-center justify-between bg-white border border-[#F5F5F5] px-3 py-1.5 text-xs">
                <span className="font-mono truncate mr-2" title={o.email}>{o.email || "(empty)"}</span>
                <span className="text-[10px] text-[#525252] tabular-nums whitespace-nowrap">
                  <strong className="text-[#DC2626]">{o.fails}</strong> fails · {o.ip_count} IP{o.ip_count === 1 ? "" : "s"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-xs text-[#A3A3A3]">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-12 text-xs text-[#A3A3A3]">
          No failed login attempts in the last {hours} hours — looking quiet 🎉
        </div>
      ) : (
        <div className="border border-[#F5F5F5] overflow-x-auto">
          <table className="w-full text-xs" data-testid="failed-logins-table">
            <thead className="bg-[#FAFAFA]">
              <tr className="text-[10px] uppercase tracking-wider text-[#525252]">
                <th className="text-left px-3 py-2 font-bold">Time</th>
                <th className="text-left px-3 py-2 font-bold">Email attempted</th>
                <th className="text-left px-3 py-2 font-bold">Reason</th>
                <th className="text-left px-3 py-2 font-bold">IP</th>
                <th className="text-left px-3 py-2 font-bold">Type</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r, i) => (
                <tr key={r.activity_id || i} className="border-t border-[#F5F5F5]">
                  <td className="px-3 py-1.5 text-[11px] tabular-nums whitespace-nowrap">{formatLocal(r.created_at, user)}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px]">{r.actor_name || "—"}</td>
                  <td className="px-3 py-1.5 text-[#991B1B]">{REASON_LABELS[r.activity_type] || r.activity_type}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-[#525252]">{r.ip_address || "—"}</td>
                  <td className="px-3 py-1.5">
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${r.actor_type === "eyvo_staff" ? "bg-blue-50 text-blue-700" : r.actor_type === "client" ? "bg-purple-50 text-purple-700" : "bg-amber-50 text-amber-700"}`}>
                      {r.actor_type}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ── Locked Accounts ──────────────────────────────────────────────────────

function LockedAccounts({ onHeads }) {
  const { user, hasRole } = useAuth();
  const canUnlock = hasRole("platform_admin");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualEmail, setManualEmail] = useState("");
  const [manualReason, setManualReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/security/locked-accounts");
      setRows(r.data?.items || []);
      if (onHeads) onHeads(r.data?.count || 0);
    } catch (e) {
      toast.error("Failed to load locked accounts");
    }
    setLoading(false);
  }, [onHeads]);

  useEffect(() => { load(); }, [load]);

  const unlock = async (email) => {
    const reason = window.prompt(`Unlock ${email}? Optionally enter a reason for the audit trail:`, "") ?? null;
    if (reason === null) return;
    try {
      await api.post("/admin/security/unlock", { email, reason });
      toast.success(`Unlocked ${email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unlock failed");
    }
  };

  const lock = async () => {
    const email = manualEmail.trim();
    const reason = manualReason.trim();
    if (!email || !reason) {
      toast.error("Email and reason required");
      return;
    }
    if (!window.confirm(`Hard-lock ${email}? They will not be able to log in until you unlock them.`)) return;
    try {
      await api.post("/admin/security/lock", { email, reason });
      toast.success(`Locked ${email}`);
      setManualEmail(""); setManualReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lock failed");
    }
  };

  return (
    <Card className="border border-[#E5E5E5] rounded-none p-5" data-testid="locked-accounts-card">
      <SectionHeader icon={Lock}
                     title="Locked Accounts"
                     subtitle="5 failed password attempts in 15 min → 30 min auto-lock. 10 in 24 h → admin unlock only."
                     onRefresh={load} refreshing={loading} />

      {loading ? (
        <div className="text-center py-8 text-xs text-[#A3A3A3]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-8 text-xs text-[#A3A3A3]">
          No locked accounts right now — authentication is healthy.
        </div>
      ) : (
        <div className="border border-[#F5F5F5] overflow-x-auto mb-5">
          <table className="w-full text-xs" data-testid="locked-accounts-table">
            <thead className="bg-[#FAFAFA]">
              <tr className="text-[10px] uppercase tracking-wider text-[#525252]">
                <th className="text-left px-3 py-2 font-bold">Email</th>
                <th className="text-left px-3 py-2 font-bold">Lock type</th>
                <th className="text-left px-3 py-2 font-bold">Fails</th>
                <th className="text-left px-3 py-2 font-bold">Last IP</th>
                <th className="text-left px-3 py-2 font-bold">Since</th>
                <th className="text-left px-3 py-2 font-bold">Until / Reason</th>
                <th className="text-right px-3 py-2 font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.email} className="border-t border-[#F5F5F5]">
                  <td className="px-3 py-1.5 font-mono text-[11px]">{r.email}</td>
                  <td className="px-3 py-1.5">
                    {r.hard_locked ? (
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200">Hard</span>
                    ) : (
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">Soft</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 tabular-nums">{r.failed_count}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-[#525252]">{r.last_ip || "—"}</td>
                  <td className="px-3 py-1.5 text-[11px] whitespace-nowrap">{formatLocal(r.last_failure_at || r.hard_locked_at, user)}</td>
                  <td className="px-3 py-1.5 text-[11px]">
                    {r.hard_locked
                      ? <span className="italic text-[#525252]">{r.hard_lock_reason || "admin unlock required"}</span>
                      : <span className="whitespace-nowrap">{formatLocal(r.locked_until, user)}</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {canUnlock ? (
                      <button onClick={() => unlock(r.email)}
                              className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF] hover:underline"
                              data-testid={`unlock-${r.email}`}>
                        <LockOpen size={10} className="inline mr-0.5" />Unlock
                      </button>
                    ) : (
                      <span className="text-[10px] text-[#A3A3A3]">view only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Manual lock panel — platform_admin only */}
      {canUnlock && (
        <div className="border border-[#FEF2F2] bg-[#FFFBFB] p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#991B1B] mb-2 flex items-center gap-1.5">
            <Warning size={12} />Manual hard-lock
          </p>
          <p className="text-xs text-[#525252] mb-3">
            Use this if you observe suspicious activity on an account. The user will not be able to log in until you unlock them.
          </p>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#525252] block mb-1">Email</label>
              <Input value={manualEmail} onChange={e => setManualEmail(e.target.value)}
                     placeholder="user@example.com"
                     className="rounded-none border-[#E5E5E5] h-8 text-xs"
                     data-testid="manual-lock-email" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#525252] block mb-1">Reason (audit-logged)</label>
              <Input value={manualReason} onChange={e => setManualReason(e.target.value)}
                     placeholder="e.g. Reported stolen laptop"
                     className="rounded-none border-[#E5E5E5] h-8 text-xs"
                     data-testid="manual-lock-reason" />
            </div>
            <Button onClick={lock}
                    className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-none h-8 px-3 text-xs uppercase tracking-wider"
                    data-testid="manual-lock-btn">
              <Lock size={12} className="mr-1.5" />Hard-lock
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── CSP Violations ───────────────────────────────────────────────────────

function CspViolations() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], top_directives: [], top_blocked_uris: [] });
  const [hours, setHours] = useState(168);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/security/csp-violations", { params: { hours, limit: 300 } });
      setData(r.data || { items: [], top_directives: [], top_blocked_uris: [] });
    } catch (e) {
      toast.error("Failed to load CSP violations");
    }
    setLoading(false);
  }, [hours]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="border border-[#E5E5E5] rounded-none p-5" data-testid="csp-violations-card">
      <SectionHeader icon={ShieldWarning}
                     title="CSP Violations"
                     subtitle="Every time a browser refuses to load a resource because of our Content-Security-Policy."
                     onRefresh={load} refreshing={loading} />

      <div className="flex gap-2 items-center mb-4">
        <label className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">Window</label>
        <select value={hours} onChange={e => setHours(Number(e.target.value))}
                className="h-8 px-2 text-xs border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]"
                data-testid="csp-hours">
          <option value={24}>24 hours</option>
          <option value={72}>3 days</option>
          <option value={168}>7 days</option>
          <option value={720}>30 days</option>
        </select>
      </div>

      {(data.top_directives?.length > 0 || data.top_blocked_uris?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          {data.top_directives?.length > 0 && (
            <div className="border border-[#E5E5E5] p-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252] mb-2">Top violated directives</p>
              <ul className="space-y-1">
                {data.top_directives.map((d, i) => (
                  <li key={i} className="flex items-center justify-between text-xs">
                    <span className="font-mono">{d.violated_directive}</span>
                    <span className="tabular-nums text-[#DC2626] font-bold">{d.n}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.top_blocked_uris?.length > 0 && (
            <div className="border border-[#E5E5E5] p-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252] mb-2">Top blocked URIs</p>
              <ul className="space-y-1">
                {data.top_blocked_uris.map((d, i) => (
                  <li key={i} className="flex items-center justify-between text-xs gap-2">
                    <span className="font-mono truncate" title={d.blocked_uri}>{d.blocked_uri}</span>
                    <span className="tabular-nums text-[#DC2626] font-bold shrink-0">{d.n}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-xs text-[#A3A3A3]">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-8 text-xs text-[#A3A3A3]">
          No CSP violations reported in the last {hours} hours.
        </div>
      ) : (
        <div className="border border-[#F5F5F5] overflow-x-auto">
          <table className="w-full text-xs" data-testid="csp-violations-table">
            <thead className="bg-[#FAFAFA]">
              <tr className="text-[10px] uppercase tracking-wider text-[#525252]">
                <th className="text-left px-3 py-2 font-bold">Time</th>
                <th className="text-left px-3 py-2 font-bold">Directive</th>
                <th className="text-left px-3 py-2 font-bold">Blocked URI</th>
                <th className="text-left px-3 py-2 font-bold">Source</th>
                <th className="text-left px-3 py-2 font-bold">Line</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(r => (
                <tr key={r.id} className="border-t border-[#F5F5F5]">
                  <td className="px-3 py-1.5 text-[11px] tabular-nums whitespace-nowrap">{formatLocal(r.received_at, user)}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px]">{r.violated_directive || "—"}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px] truncate max-w-[280px]" title={r.blocked_uri}>{r.blocked_uri || "—"}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px] truncate max-w-[220px]" title={r.source_file}>{r.source_file || "—"}</td>
                  <td className="px-3 py-1.5 tabular-nums text-[11px] text-[#525252]">{r.line_number || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ── Top-level tab ────────────────────────────────────────────────────────

export default function SecurityTab() {
  const [sub, setSub] = useState("failed");
  const [lockedCount, setLockedCount] = useState(0);

  return (
    <div className="space-y-6" data-testid="security-tab">
      <SecuritySummaryWidget onJumpTo={(id) => setSub(id)} />
      <div className="flex gap-0 border-b border-[#E5E5E5] mb-0">
        {[
          { id: "failed", label: "Failed Logins" },
          { id: "locked", label: `Locked Accounts${lockedCount ? ` (${lockedCount})` : ""}` },
          { id: "csp", label: "CSP Violations" },
        ].map(t => (
          <button key={t.id}
                  onClick={() => setSub(t.id)}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${sub === t.id ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#525252] hover:text-[#0A0A0A]"}`}
                  data-testid={`security-subtab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>
      {sub === "failed" && <FailedLogins />}
      {sub === "locked" && <LockedAccounts onHeads={setLockedCount} />}
      {sub === "csp" && <CspViolations />}
    </div>
  );
}
